import { EqlFieldsAutoComplete } from '@modules/common/components/EqlFieldsAutoComplete';
import { EqlFieldsAutoCompleteOption } from '@modules/common/components/EqlFieldsAutoCompleteOption';
import { DEFAULT_QUANTIFY_SORT_BY } from '@modules/quantify/constants';
import {
  useFetchMeasuredAsCriteria,
  useFetchQuantifyReport,
  useGetQuantifyFormulaDisplayName,
  useTriggerQuantifyQueryAnalyticsEvent,
} from '@modules/quantify/hooks';
import { QuantifyActionType } from '@modules/quantify/store/quantifyActionType';
import { useQuantifyStore } from '@modules/quantify/store/quantifyStore';
import { QuantifyState } from '@modules/quantify/store/types';
import { isAggregateFormula } from '@modules/quantify/utils';
import ArrowDropDown from '@mui/icons-material/ArrowDropDown';
import {
  AutocompleteRenderInputParams,
  AutocompleteRenderOptionState,
  Box,
  SxProps,
  TextField,
  Theme,
  Tooltip,
} from '@mui/material';
import {
  EqlField,
  EqlFieldValueType,
  ObjectType,
  QuantifyVisualisationType,
} from 'generated/graphql';
import { SyntheticEvent, useEffect, useState } from 'react';
import { EQLFunctionName } from 'v2/core/repository/eql/types';

interface MeasuredAsCriteriaAutoCompleteProps {
  sx?: SxProps<Theme>;
}

export function MeasuredAsCriteriaAutoComplete({
  sx,
}: MeasuredAsCriteriaAutoCompleteProps) {
  const dispatch = useQuantifyStore(({ dispatch }) => dispatch);
  const groupBy = useQuantifyStore(({ query }) => query.groupByField);
  const formulaID = useQuantifyStore(
    ({ query }) => query.formulaQuery?.eqlFunction,
  );
  const measuredAsCriteria = useQuantifyStore(
    ({ query }) => query.formulaQuery?.criteria,
  );
  const { criteriaList, loading } = useFetchMeasuredAsCriteria({
    field:
      isAggregateFormula(formulaID) &&
      groupBy?.valueType === EqlFieldValueType.Number
        ? groupBy
        : undefined,
  });
  const [shouldOpenDropdown, setShouldOpenDropdown] = useState(false);
  const fetchReportData = useFetchQuantifyReport();
  const [shouldFetchReportData, setShouldFetchReportData] = useState(false);
  const [inputElement, setInputElement] = useState<HTMLInputElement | null>(
    null,
  );
  const { formulaDisplayName } = useGetQuantifyFormulaDisplayName();
  const trackQuantifyQueryEvents = useTriggerQuantifyQueryAnalyticsEvent();

  useEffect(() => {
    if (groupBy && formulaID) {
      setShouldOpenDropdown(!measuredAsCriteria);

      if (!measuredAsCriteria) {
        inputElement?.click();
      }
    }
  }, [measuredAsCriteria, formulaID, groupBy, inputElement]);

  useEffect(() => {
    if (shouldFetchReportData) {
      setShouldFetchReportData(false);
      fetchReportData();
      trackQuantifyQueryEvents('Quantify - Query Executed');
    }
  }, [fetchReportData, shouldFetchReportData, trackQuantifyQueryEvents]);

  const onOpen = () => {
    setShouldOpenDropdown(true);
  };

  const onClose = () => {
    setShouldOpenDropdown(false);
  };

  function onChange(_: SyntheticEvent, eqlField: EqlField | undefined) {
    dispatch({
      type: QuantifyActionType.SET_QUERY,
      payload: {
        formulaQuery: {
          criteria: eqlField,
        },
        sortBy: DEFAULT_QUANTIFY_SORT_BY,
      },
    });

    const queryPayload: Partial<QuantifyState['query']> = {
      visibleDataPoints: [],
      shouldShowTopDataPoints: true,
    };

    if (eqlField?.objectType && eqlField?.objectType !== ObjectType.Feedback) {
      queryPayload['visualisationType'] = QuantifyVisualisationType.Bar;
    }

    dispatch({
      type: QuantifyActionType.SET_QUERY,
      payload: queryPayload,
    });

    setShouldFetchReportData(true);
    trackQuantifyQueryEvents('Quantify - Measured As Selected', {
      measuredAsCriteria: eqlField?.displayName,
    });
  }

  const getOptionDisabled = (option: EqlField): boolean => {
    if (
      formulaID === EQLFunctionName.Count ||
      formulaID === EQLFunctionName.NormaliseByPercent
    ) {
      return option.valueType !== EqlFieldValueType.None;
    }

    return option.valueType !== EqlFieldValueType.Number;
  };

  const helperText =
    !measuredAsCriteria && formulaDisplayName
      ? `Select what you would like to measure the ${formulaDisplayName} of`
      : undefined;

  const renderOption = (
    props: React.HTMLAttributes<HTMLLIElement>,
    option: EqlField,
    state: AutocompleteRenderOptionState,
  ) => {
    return (
      <Tooltip
        arrow
        title={
          props['aria-disabled'] && formulaDisplayName
            ? `This criteria isn't available to calculate ${formulaDisplayName}.`
            : undefined
        }
        placement="left"
        key={`${option.field}_tooltip`}
      >
        <Box>
          <EqlFieldsAutoCompleteOption
            inputValue={state.inputValue}
            getOptionDisplayName={(option) => option.displayName}
            props={props}
            option={option}
          />
        </Box>
      </Tooltip>
    );
  };

  return (
    <EqlFieldsAutoComplete
      id="quantify-measured-as-criteria"
      autoHighlight
      autoFocus
      open={shouldOpenDropdown}
      onOpen={onOpen}
      onClose={onClose}
      value={measuredAsCriteria}
      options={criteriaList}
      disabled={!groupBy}
      onChange={(event, value) => onChange(event, value ?? undefined)}
      sx={{ ...sx }}
      renderOption={renderOption}
      renderInput={(params: AutocompleteRenderInputParams) => (
        <TextField
          {...params}
          helperText={helperText}
          inputRef={(input) => {
            setInputElement(input);
          }}
          required
        />
      )}
      popupIcon={<ArrowDropDown />}
      getOptionDisabled={getOptionDisabled}
      loading={loading}
    />
  );
}
